import React from 'react';
import { Box, Typography, InputBase, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useFormPalette } from '../pages/forms/DynamicFormPrimitives';
import { NODE_UI } from '../NodeUI/nodeUiStyles';
import { formatPlaceholderKey } from '../NodeUI/placeholderKeyUtils';
import { getSystemOutputForType } from '../../utils/creationOutputPlaceholder';

const CREATION_PORT_LABELS = {
  dynamic_review_task: 'On creation port',
  ai_agent: 'On agent session created port',
  form_step: 'On step started port',
};

const VALUE_DESCRIPTIONS = {
  dynamic_review_task: "the respondent's task URL",
  ai_agent: 'a link to the agent session',
  form_step: 'the form-flow URL for this step',
};

/**
 * Inline rename UI for the renameable creation-edge output placeholder a
 * wait-state component exposes (see CreationPlaceholderConfig on the
 * engine, creationOutputPlaceholder helpers on the frontend).
 *
 * Slotted into each component drawer's Settings tab. Defaults are auto-
 * generated unique keys (mirrors useAIAgentStore.chatSessionId), so on
 * first creation the field is already populated with something safe to
 * use; the author can rename to whatever reads best in the downstream
 * Tiptap editors.
 */
export default function CreationOutputPlaceholderField({
  componentType,
  value,
  onChange,
}) {
  const palette = useFormPalette();
  const systemOutput = getSystemOutputForType(componentType);
  const portLabel = CREATION_PORT_LABELS[componentType] || 'On creation port';
  const valueDesc = VALUE_DESCRIPTIONS[componentType] || 'a runtime URL';
  const currentKey = value?.key || '';

  // Sanitize on every keystroke so the rendered hint and the saved value
  // stay in lock-step with what the engine actually accepts (letters,
  // digits, underscore, colon — same rules used elsewhere for placeholder
  // keys via NodeUI's placeholderKeyUtils).
  const handleChange = (e) => {
    onChange(formatPlaceholderKey(e.target.value));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Typography
          component="label"
          sx={{
            fontSize: NODE_UI.fontSize.label,
            fontWeight: NODE_UI.fontWeight.label,
            color: palette.textSecondary,
            lineHeight: 1.4,
            userSelect: 'none',
          }}
        >
          Placeholder name
        </Typography>
        <Tooltip
          title={`Resolves to ${valueDesc} when an action is triggered through the ${portLabel}. Auto-generated to be unique; rename to whatever reads best downstream.`}
          placement="top"
          arrow
        >
          <InfoOutlinedIcon
            sx={{
              fontSize: 13,
              color: palette.textSecondary,
              opacity: 0.5,
              cursor: 'help',
            }}
          />
        </Tooltip>
      </Box>
      <InputBase
        value={currentKey}
        onChange={handleChange}
        placeholder={systemOutput || 'placeholder_name'}
        fullWidth
        inputProps={{
          spellCheck: false,
          autoCapitalize: 'off',
          autoCorrect: 'off',
        }}
        sx={{
          fontFamily: 'monospace',
          fontSize: NODE_UI.fontSize.value,
          color: palette.textPrimary,
          border: `1px solid ${palette.border}`,
          borderRadius: NODE_UI.borderRadiusSm,
          px: '8px',
          py: '5px',
          '&:hover': { borderColor: palette.textSecondary },
          '&.Mui-focused': { borderColor: palette.textPrimary },
          '& input::placeholder': { color: palette.textSecondary, opacity: 0.5 },
        }}
      />
      <Typography
        sx={{
          fontSize: NODE_UI.fontSize.caption,
          color: palette.textSecondary,
          opacity: 0.7,
        }}
      >
        Available as <code>{`\${${currentKey || systemOutput || 'placeholder_name'}}`}</code>{' '}
        on actions wired through the <strong>{portLabel}</strong>.
      </Typography>
    </Box>
  );
}
